import React, { useState } from 'react';
import { Box, Link, IconButton, Menu, MenuItem, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Menu as MenuIcon } from '@mui/icons-material';
import { buildWebSiteUrl } from '../../utils/formatHelpers';

interface LightMarketingHeaderProps {
  hideNavigation?: boolean;
  minimal?: boolean;
  selectedPage?: string;
}

const LightMarketingHeader: React.FC<LightMarketingHeaderProps> = ({ hideNavigation = false, minimal = false, selectedPage = 'home' }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleExternalNavigate = (url: string) => {
    window.location.href = url;
    handleMenuClose();
  };

  const menuItems = [
    { label: 'Home', path: buildWebSiteUrl('/'), id: 'home' },
    { label: 'For Creators', path: buildWebSiteUrl('/for-creators'), id: 'creators' },
    { label: 'For Bettors', path: buildWebSiteUrl('/for-bettors'), id: 'bettors' },
    { label: 'Pricing', path: buildWebSiteUrl('/pricing'), id: 'pricing' },
    { label: 'Blog', path: buildWebSiteUrl('/blog'), id: 'blog' },
    { label: 'About', path: buildWebSiteUrl('/about'), id: 'about' },
    { label: 'Investors', path: buildWebSiteUrl('/investors'), id: 'investors' },
  ];

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      pt: minimal ? 1.5 : 3,
      px: { xs: 2, md: 3 },
      position: 'sticky',
      top: 0,
      zIndex: 10,
      backgroundColor: 'transparent',
    }}>
      {/* Floating pill nav */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: '999px',
        px: { xs: 2, sm: 3, md: 3 },
        pr: { md: 1.5 },
        py: 1.25,
        boxShadow: '0 2px 16px rgba(0, 0, 0, 0.06)',
        border: '1px solid #F0F0F0',
      }}>
        {/* Logo */}
        <Box
          component="button"
          onClick={() => navigate('/')}
          sx={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <Box
            component="img"
            src="/assets/images/logo-standard.png"
            alt="Moddy AI"
            sx={{
              width: '100px',
              height: 'auto',
              mr: 3,
            }}
          />
        </Box>

        {/* Navigation */}
        {!hideNavigation && (
          <>
            {/* Desktop Menu */}
            <Box sx={{
              display: { xs: 'none', md: 'flex' },
              gap: { md: 2, lg: 3 },
              alignItems: 'center',
            }}>
              {menuItems.map((item) => (
                <Link
                  key={item.id}
                  onClick={() => item.path === '#' ? undefined : handleExternalNavigate(item.path)}
                  sx={{
                    cursor: item.path === '#' ? 'default' : 'pointer',
                    color: '#1d1d1f',
                    fontFamily: '"Assistant", sans-serif',
                    fontWeight: 600,
                    textDecoration: 'none',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    position: 'relative',
                    overflow: 'hidden',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '999px',
                    zIndex: 1,
                    transition: 'color 0.3s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: '#1d1d1f',
                      borderRadius: '999px',
                      transformOrigin: 'top',
                      transform: 'scaleY(0)',
                      transition: 'transform 0.3s ease',
                      zIndex: -1,
                    },
                    '&:hover': {
                      color: '#FFFFFF',
                      '&::before': {
                        transformOrigin: 'bottom',
                        transform: 'scaleY(1)',
                      },
                    },
                  }}
                >
                  {item.label}
                </Link>
              ))}
              <Button
                onClick={() => navigate('/login')}
                variant="contained"
                sx={{
                  borderRadius: '999px',
                  backgroundColor: '#E8E8E8',
                  color: '#1d1d1f',
                  fontSize: '14px',
                  fontFamily: '"Assistant", sans-serif',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 3,
                  py: 0.75,
                  minWidth: 'auto',
                  boxShadow: 'none',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#DBDBDB',
                    boxShadow: 'none',
                  },
                }}
              >
                Login
              </Button>
            </Box>

            {/* Mobile Menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                onClick={handleMenuOpen}
                sx={{
                  color: '#1A1A1A',
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #F0F0F0',
                    borderRadius: '16px',
                    mt: 1,
                    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                {menuItems.map((item) => (
                  <MenuItem
                    key={item.id}
                    onClick={() => item.path === '#' ? handleMenuClose() : handleExternalNavigate(item.path)}
                    sx={{
                      color: selectedPage === item.id ? '#1A1A1A' : '#4B5563',
                      fontWeight: selectedPage === item.id ? 600 : 400,
                      fontSize: '0.875rem',
                      py: 1.5,
                      px: 3,
                      minWidth: 200,
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                      }
                    }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={() => handleNavigate('/login')}
                  sx={{
                    color: '#1A1A1A',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    py: 1.5,
                    px: 3,
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Login
                </MenuItem>
              </Menu>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LightMarketingHeader;
